import React, { useEffect, useState, useRef } from 'react';
import './WhereToBuy.css';
import { BASE_URL } from "../path";

interface Outlet {
    Name: string;
    Latitude: number;
    Longitude: number;
    Website: string;
    IsRetail: boolean;
    IsOnline: boolean;
    IsRestaurant: boolean;
    AddressLine1: string;
    AddressLine2: string;
    AddressLine3: string;
    AddressLine4: string;
    AddressLine5: string;
    AddressLine6: string;
    AddressPostalCode: string;
    DistanceFromSearchPoint_Metres: number;
}

interface Province {
    Name: string;
}

interface Town {
    Name: string;
    Province: string;
}

interface WhereToBuyProps {
    isOpen: boolean;
    onClose: () => void;
    brand?: string;
}

declare global {
    interface Window {
        google: any;
    }
}

const GOOGLE_API_KEY = 'AIzaSyDZ9HsWjdnCUf_3_ASESBpK7gVIVyX4g2U';
const DEFAULT_LAT = -33.93415799000691;
const DEFAULT_LNG = 18.851194299451087;

const WhereToBuy: React.FC<WhereToBuyProps> = ({ isOpen, onClose, brand }) => {
    const [useCurrentLocation, setUseCurrentLocation] = useState(true);
    const [lat, setLat] = useState<number | null>(null);
    const [lng, setLng] = useState<number | null>(null);
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [towns, setTowns] = useState<Town[]>([]);
    const [selectedProvince, setSelectedProvince] = useState<string>('*');
    const [filteredTowns, setFilteredTowns] = useState<Town[]>([]);
    const [selectedTown, setSelectedTown] = useState<string>('*');
    const [outlets, setOutlets] = useState<Outlet[]>([]);
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstance = useRef<any>(null);
    const markers = useRef<any[]>([]);


    // Load Google Maps script dynamically
    useEffect(() => {
        if (!window.google) {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}`;
            script.async = true;
            script.defer = true;
            script.onload = () => {

            };
            document.head.appendChild(script);
        }
    }, []);

    // On open, set default coordinates if lat/lng null
    useEffect(() => {
        if (isOpen) {
            if (lat === null || lng === null) {
                setLat(DEFAULT_LAT);
                setLng(DEFAULT_LNG);
            }
        }
    }, [isOpen]);

    // When lat/lng or useCurrentLocation changes and isOpen, fetch outlets automatically
    useEffect(() => {
        if (!isOpen) return;
        if (!brand?.trim()) return;

        if (useCurrentLocation && lat !== null && lng !== null) {
            findOutlets();
        }
    }, [isOpen, useCurrentLocation, lat, lng, brand]);

    // Initialize geolocation if using current location and panel is open
    useEffect(() => {
        if (isOpen && useCurrentLocation) {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        setLat(pos.coords.latitude);
                        setLng(pos.coords.longitude);
                    },
                    (err) => {
                        console.warn('Geolocation failed or denied, switching to province');
                        setUseCurrentLocation(false);
                    },
                    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
                );
            } else {
                setUseCurrentLocation(false);
            }
        }
    }, [isOpen, useCurrentLocation]);

    // Fetch provinces and towns on open
    useEffect(() => {
        if (!isOpen) return;

        fetch(BASE_URL + '/proxy', {
            method: 'GET',
            headers: {
                'X-Proxy-To': 'https://app.vinimark.co.za:443/where-to-buy/api/SearchOptions/ProvincesAndTowns',
                Authorization: 'Basic dmluaW1hcms6ZWZiNjI5Yjk=',
            },
        })
            .then((res) => res.json())
            .then((data) => {
                setProvinces(data.Provinces || []);
                setTowns(data.Towns || []);
            })
            .catch(() => {
                setProvinces([]);
                setTowns([]);
            });
    }, [isOpen]);

    // Filter towns by selected province
    useEffect(() => {
        if (selectedProvince === '*' || selectedProvince === '') {
            setFilteredTowns(towns);
        } else {
            setFilteredTowns(towns.filter((town) => town.Province === selectedProvince));
        }
        setSelectedTown('*');
    }, [selectedProvince, towns]);
    const linkStyle: React.CSSProperties = {
        color: 'black',
        fontSize: '10px',
        fontFamily: "'Vinimark-Diatype', sans-serif",
        fontWeight: 600,
        textTransform: 'uppercase',
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
        cursor: 'pointer',
        lineHeight: '13px'
    };

    // Initialize / reset map on outlets change
    useEffect(() => {
        if (!mapRef.current) return;

        // Clear old markers
        markers.current.forEach((marker) => marker.setMap(null));
        markers.current = [];

        if (outlets.length === 0) {
            if (mapInstance.current) {
                mapInstance.current = null;
            }
            return;
        }

        // Center map based on first retail outlet or first outlet
        const retailOutlets = outlets.filter((o) => o.IsRetail);
        const centerOutlet = retailOutlets.length > 0 ? retailOutlets[0] : outlets[0];

        const center = { lat: centerOutlet.Latitude, lng: centerOutlet.Longitude };
        const zoom = outlets.length > 17 ? 8 : 10;

        mapInstance.current = new window.google.maps.Map(mapRef.current, {
            center,
            zoom,
        });

        // Add markers
        outlets.forEach((outlet) => {
            if (outlet.Latitude === 0 && outlet.Longitude === 0) return;

            const marker = new window.google.maps.Marker({
                position: { lat: outlet.Latitude, lng: outlet.Longitude },
                map: mapInstance.current,
                title: outlet.Name,
                icon: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                animation: window.google.maps.Animation.DROP,
            });

            marker.addListener('click', () => {
                const url = `https://www.google.com/maps/place/${outlet.Latitude},${outlet.Longitude}`;
                window.open(url, '_blank');
            });

            markers.current.push(marker);
        });
    }, [outlets]);

    const findOutlets = () => {
        if (!brand?.trim()) {
            alert('Brand not provided');
            return;
        }
        const supplier = brand.trim();
        let url = '';
        let params = new URLSearchParams();

        if (useCurrentLocation) {
            if (lat === null || lng === null) {
                alert('Waiting for location or location not available');
                return;
            }
            url = 'https://app.vinimark.co.za:443/where-to-buy/api/FindOutlets/ByCoordinates_ForSupplier';
            params.append('latitude', lat.toString());
            params.append('longitude', lng.toString());
            params.append('supplier', supplier);
        } else {
            url = 'https://app.vinimark.co.za:443/where-to-buy/api/FindOutlets/BySearch_ForSupplier';
            params.append('province', selectedProvince);
            params.append('town', selectedTown);
            params.append('supplier', supplier);
        }

        fetch(BASE_URL + '/proxy?' + params.toString(), {
            method: 'GET',
            headers: {
                'X-Proxy-To': url,
                Authorization: 'Basic dmluaW1hcms6ZWZiNjI5Yjk=',
            },
        })
            .then((res) => res.json())
            .then((data) => {
                setOutlets(data.Outlets || []);

            })
            .catch(() => {
                setOutlets([]);
                // optionally alert or handle error here
            });
    };

    useEffect(() => {
        console.log('Outlets:', outlets);
    }, [outlets]);

    if (!isOpen) return null;

    return (
        <div className="where-to-buy-panel open">
            <button className="close-button" onClick={onClose} aria-label="Close">
                &times;
            </button>

            <div className="where-to-buy-content" style={{overflowY: 'auto'}}>
                <div className="search-form-section" >
                    <div className="search-options">
                        <p style={{ marginBottom: '13px', fontWeight: 600, textTransform: 'uppercase', textAlign: 'left' }}>{brand ? brand : ''}</p>
                        <label style={{ fontSize: '13px', lineHeight: '16.9px', textAlign: 'left'  }}>
                            <input
                                type="radio"
                                checked={useCurrentLocation}
                                onChange={() => setUseCurrentLocation(true)}
                                style={{ fontSize: '13px', lineHeight: '16.9px'}}
                            />
                            Use my current location
                        </label>
                        <label style={{ fontSize: '13px', lineHeight: '16.9px', textAlign: 'left' }}>
                            <input
                                type="radio"
                                checked={!useCurrentLocation}
                                onChange={() => setUseCurrentLocation(false)}
                            />
                            Search by location
                        </label>

                        {!useCurrentLocation && (
                            <>
                                <div className="form-group">
                                    <label style={{textAlign: 'left', fontSize: '13px', lineHeight: '16.9px'}}>
                                        Province:
                                        <select style={{fontSize: '13px', lineHeight: '16.9px', color: 'black'}} value={selectedProvince} onChange={(e) => setSelectedProvince(e.target.value)}>
                                            <option value="*">* - All</option>
                                            {provinces.map((prov) => (
                                                <option key={prov.Name} value={prov.Name}>
                                                    {prov.Name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label style={{textAlign: 'left', fontSize: '13px', lineHeight: '16.9px'}}>
                                        Town/City:
                                        <select style={{fontSize: '13px', lineHeight: '16.9px', color: 'black'}} value={selectedTown} onChange={(e) => setSelectedTown(e.target.value)}>
                                            <option value="*">* - All</option>
                                            {filteredTowns.map((town) => (
                                                <option key={town.Name} value={town.Name}>
                                                    {town.Name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            </>
                        )}

                        <button className="btn-find-outlets" onClick={() => findOutlets()}>
                            Find Outlets
                        </button>

                        <div style={{ overflowY: 'scroll' }}>
                            {outlets.length === 0 && <p>No outlets found.</p>}
                            {outlets.map((outlet) => {
                                const showDistance =
                                    outlet.IsRetail && outlet.DistanceFromSearchPoint_Metres !== null && outlet.DistanceFromSearchPoint_Metres !== undefined;

                                return (
                                    <div key={outlet.Name} className="outlet-box">
                                        <p className={'outlet-name'} style={{ marginTop: 0, marginBottom: 0, textAlign: 'left' }}>{outlet.Name}</p>
                                        <p style={{ marginTop: 0, marginBottom: 0, textAlign: 'left' }}>
                                            {outlet.AddressLine1} {outlet.AddressLine2} {outlet.AddressLine3}
                                        </p>
                                        {showDistance && (
                                            <p style={{ marginTop: 0, marginBottom: 0, textAlign: 'left' }}>
                                                {`${(outlet.DistanceFromSearchPoint_Metres / 1000).toFixed(1)} km away`}
                                            </p>
                                        )}
                                        {outlet.Website && (
                                            <p style={{ marginTop: 0, marginBottom: 0, textAlign: 'left' }}>
                                                <a href={outlet.Website} target="_blank" rel="noreferrer" style={linkStyle}>
                                                    Website
                                                </a>
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>


                    </div>
                </div>

                <div className="map-section">
                    <div ref={mapRef} className="map-container"></div>
                </div>
            </div>

            <div className="outlet-results">

            </div>
        </div>
    );
};

export default WhereToBuy;
